import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import BidHistoryModal, { BidItem } from '../components/BidHistoryModal';
import { useSeo } from '../hooks/useSeo';
import { SITE_URL, buildTitle } from '../constants/seo';

interface TopBidder {
  displayName: string;
  twitchUsername?: string;
  amount: number;
}

interface Auction {
  _id: string;
  title: string;
  description: string;
  imageUrl: string;
  startingPrice: number;
  currentHighBid: number;
  status: string;
  closedAt?: string;
  endsAt?: string;
  updatedAt?: string;
  topBidder: TopBidder | null;
  recentBids: BidItem[];
  bidCount: number;
}

function formatArs(amount: number) {
  return `$${amount.toLocaleString('es-AR')}`;
}

function formatAuctionDate(iso?: string) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('es-AR', {
    timeZone: 'America/Argentina/Buenos_Aires',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function twitchHandle(bidder: TopBidder) {
  const login = (bidder.twitchUsername || '').replace(/^@/, '').trim();
  return login ? `@${login}` : bidder.displayName;
}

const AuctionsPage: React.FC = () => {
  useSeo({
    title: buildTitle('Subastas'),
    description: 'Subastas en vivo de la comunidad Eugenialitos. Pujá con tu cuenta de Twitch y participá en los sorteos de EuGe_NiAL.',
    url: `${SITE_URL}/subastas`,
  });

  const [auctions, setAuctions] = useState<Auction[]>([]);
  const [modalBids, setModalBids] = useState<BidItem[]>([]);
  const [modalTitle, setModalTitle] = useState('');

  const load = () => {
    api.get('/auctions').then(({ data }) => setAuctions(data));
  };

  useEffect(() => { load(); }, []);

  const openFullHistory = async (auction: Auction) => {
    const { data } = await api.get(`/auctions/${auction._id}`);
    setModalTitle(`Pujas · ${auction.title}`);
    setModalBids(data.bids);
  };

  const activeAuctions = auctions.filter((a) => a.status !== 'closed');
  const closedAuctions = [...auctions.filter((a) => a.status === 'closed')].sort((a, b) => {
    const aTime = new Date(a.closedAt || a.endsAt || a.updatedAt || 0).getTime();
    const bTime = new Date(b.closedAt || b.endsAt || b.updatedAt || 0).getTime();
    return bTime - aTime;
  });

  return (
    <div className="relative bg-gradient-to-b from-[#0d0a1a] to-[#0a0815] py-16 px-6 min-h-[70vh]">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-rose-300/60 text-sm font-medium tracking-[0.2em] uppercase mb-3">Comunidad</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white/90">Subastas</h1>
          <p className="mt-4 text-slate-400 text-base max-w-xl mx-auto">
            Obras disponibles para pujar. Necesitás iniciar sesión con Twitch y seguir el canal al menos 3 meses.
          </p>
        </div>

        <section>
          {activeAuctions.length > 0 && (
            <h2 className="sr-only">Subastas activas</h2>
          )}
          <div className="grid sm:grid-cols-2 gap-6">
            {activeAuctions.map((a) => (
              <article
                key={a._id}
                className="flex flex-col rounded-2xl border border-rose-400/20 bg-gradient-to-br from-rose-400/10 to-pink-400/5 hover:border-rose-400/40 transition-all duration-300 overflow-hidden"
              >
                <Link to={`/subastas/${a._id}`} className="block">
                  <div className="aspect-[4/3] bg-black/30 flex items-center justify-center overflow-hidden">
                    <img src={a.imageUrl} alt={a.title} className="max-w-full max-h-full object-contain p-2" />
                  </div>
                  <div className="p-5 pb-3">
                    <h2 className="text-lg font-semibold text-white/90 mb-1">{a.title}</h2>
                    {a.description && (
                      <p className="text-sm text-slate-400/80 line-clamp-2 whitespace-pre-wrap">{a.description}</p>
                    )}
                  </div>
                </Link>

                <div className="px-5 pb-3">
                  <p className="text-sm text-white/90">
                    {a.topBidder ? (
                      <>Líder: <strong className="text-rose-200">{a.topBidder.displayName}</strong> · {formatArs(a.topBidder.amount)}</>
                    ) : (
                      <>Sin pujas · base {formatArs(a.startingPrice)}</>
                    )}
                  </p>

                  {a.recentBids.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-white/10">
                      <p className="text-xs text-slate-500 mb-2">Últimas pujas</p>
                      {a.recentBids.map((b) => (
                        <div key={b._id} className="flex justify-between text-xs text-slate-400 py-0.5">
                          <span>{b.displayName}</span>
                          <span>{formatArs(b.amount)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="mt-auto p-5 pt-0 flex flex-wrap gap-2">
                  <Link
                    to={`/subastas/${a._id}`}
                    className="inline-flex px-4 py-2 rounded-xl text-sm font-medium bg-gradient-to-r from-rose-400/90 to-pink-500/90 text-white hover:opacity-90 transition-opacity"
                  >
                    Pujar
                  </Link>
                  {a.bidCount > 3 && (
                    <button
                      type="button"
                      onClick={() => openFullHistory(a)}
                      className="inline-flex px-4 py-2 rounded-xl text-sm border border-white/15 text-slate-300 hover:text-white hover:border-white/30 transition-colors"
                    >
                      Ver más ({a.bidCount})
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>

          {!activeAuctions.length && (
            <p className="text-center text-slate-500">No hay subastas activas por ahora.</p>
          )}
        </section>

        {closedAuctions.length > 0 && (
          <section className="mt-16 pt-12 border-t border-white/10">
            <div className="text-center mb-8">
              <p className="text-slate-500 text-xs font-medium tracking-[0.2em] uppercase mb-2">Archivo</p>
              <h2 className="text-2xl sm:text-3xl font-bold text-white/80">Subastas finalizadas</h2>
              <p className="mt-2 text-slate-500 text-sm max-w-lg mx-auto">
                Obras que ya cerraron. Acá se ve quién se las llevó, cuándo y por cuánto.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              {closedAuctions.map((a) => {
                const closedLabel = formatAuctionDate(a.closedAt || a.endsAt || a.updatedAt);
                return (
                  <article
                    key={a._id}
                    className="flex flex-col rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden"
                  >
                    <Link to={`/subastas/${a._id}`} className="block">
                      <div className="relative aspect-[4/3] bg-black/30 flex items-center justify-center overflow-hidden">
                        <img
                          src={a.imageUrl}
                          alt={a.title}
                          className="max-w-full max-h-full object-contain p-2 opacity-80 grayscale-[30%]"
                        />
                        <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-[11px] font-semibold tracking-wide text-slate-200 bg-black/55 border border-white/15">
                          Finalizada
                        </span>
                      </div>
                      <div className="p-5 pb-3">
                        <h3 className="text-lg font-semibold text-white/80 mb-3">{a.title}</h3>
                        {a.topBidder ? (
                          <div className="space-y-1.5 text-sm">
                            <p className="text-slate-300 m-0">
                              Se la llevó{' '}
                              <strong className="text-rose-200/90">{twitchHandle(a.topBidder)}</strong>
                            </p>
                            <p className="text-white/90 m-0">
                              Monto final <strong>{formatArs(a.topBidder.amount)}</strong>
                            </p>
                          </div>
                        ) : (
                          <p className="text-sm text-slate-400 m-0">
                            Finalizó sin pujas · base {formatArs(a.startingPrice)}
                          </p>
                        )}
                        {closedLabel && (
                          <p className="text-xs text-slate-500 mt-3 m-0">Cerrada el {closedLabel}</p>
                        )}
                      </div>
                    </Link>

                    <div className="mt-auto p-5 pt-0">
                      <Link
                        to={`/subastas/${a._id}`}
                        className="inline-flex px-4 py-2 rounded-xl text-sm border border-white/15 text-slate-300 hover:text-white hover:border-white/30 transition-colors"
                      >
                        Ver resultado
                      </Link>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        )}
      </div>

      {modalBids.length > 0 && (
        <BidHistoryModal title={modalTitle} bids={modalBids} onClose={() => setModalBids([])} />
      )}
    </div>
  );
};

export default AuctionsPage;
